import { animate, style, transition, trigger } from '@angular/animations';
import {
  Component,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { User } from 'src/app/models/user';
import {
  DevicesManagerService,
  deviceHasPermissionError,
} from 'src/app/services/devices-manager.service';
import { PresenceProvider } from 'src/app/services/presence-provider';
import { RtcServiceController } from 'src/app/services/rtc.service';
import { UiService, ViewType } from 'src/app/services/ui.service';
import { UserService } from 'src/app/services/user.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { PROMPTS } from 'src/app/common/utils/call-states-constants';
import { VirtualBackgroundInsertableStreamService } from 'src/app/services/virtual-background-insertable-stream.service';
import {
  DevicesSettingsBaseComponent,
  DevicesType,
} from 'src/app/sessions/session/wb-video-controls/wb-video-controls-buttons/devices-settings/devices-settings-base.component';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { AudioService } from 'src/app/services/audio.service';
import { DeviceAndBrowserDetectorService } from 'src/app/services/device-and-browser-detector.service';
import {
  CallRelatedModalAction,
  DeviceErrorType,
  DeviceKind,
  DeviceState,
  JoinCallDevicesState,
} from 'src/app/models/device-manger';
import { TranslateService } from '@ngx-translate/core';
import { CustomerSupportService } from 'src/app/services/customer-support.service';
import { SessionSharedDataService } from 'src/app/services/session-shared-data.service';
import { LocalTracksManagerService } from 'src/app/services/local-tracks-manager.service';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { OBSERVER_MODE_LEARN_MORE_URL } from 'src/app/services/observer-mode.utils';
import { SpaceRepository } from 'src/app/state/space.repository';
import { filterNil } from '@ngneat/elf';
import { combineLatest, distinctUntilChanged, map, take } from 'rxjs';
import { RTCDevices } from 'src/app/common/interfaces/rtc-interface';
import { NoiseCancellationConfigurationService } from 'src/app/services/noise-cancellation-configuration.service';
import { ChildModeService } from 'src/app/services/child-mode.service';
import { MusicModeConfigurationService } from 'src/app/services/music-mode-configuration.service';
import { ColorScheme } from 'src/app/standalones/components/pencil-bottom-sheet-container/pencil-bottom-sheet-container.component';
import { CamButtonStateService } from 'src/app/sessions/session/services/cam-button-state.service';
import { MicButtonStateService } from 'src/app/sessions/session/services/mic-button-state.service';
import { GUIDES } from '../steps-guide/steps-guide.component';
import { intercomArticles } from '../../intercom-articles';
import { WbDialogService } from '../../services/wb-dialog.service';
const enterTransition = transition(':enter', [
  style({
    opacity: 0,
  }),
  animate(
    '0.3s ease-in',
    style({
      opacity: 1,
    }),
  ),
]);

const leaveTrans = transition(':leave', [
  style({
    opacity: 1,
  }),
  animate(
    '0.3s ease-out',
    style({
      opacity: 0,
    }),
  ),
]);

const fadeIn = trigger('fadeIn', [enterTransition]);

const fadeOut = trigger('fadeOut', [leaveTrans]);

@UntilDestroy()
@Component({
  selector: 'app-join-call-panel',
  templateUrl: './join-call-panel.component.html',
  styleUrls: ['./join-call-panel.component.scss'],
  animations: [fadeIn, fadeOut],
})
export class JoinCallPanelComponent
  extends DevicesSettingsBaseComponent
  implements OnInit, OnDestroy
{
  readonly DEFAULT_HOST_STARTED_CALL = this.translateService.instant('A host has started call');
  readonly CallRelatedModalActionEnum = CallRelatedModalAction;
  readonly DeviceKind = DeviceKind;
  readonly SHOW_DEVICE_IS_ON_TOAST_PERIOD = 500;
  readonly observerModeLearnMoreUrl = OBSERVER_MODE_LEARN_MORE_URL;
  readonly showObserverModeNotice$ = combineLatest([
    this.flagsService.featureFlagChanged(FLAGS.SPACES_OBSERVER_MODE),
    this.spaceRepository.activeSpaceId$.pipe(
      distinctUntilChanged(),
      filterNil(),
      map(
        () =>
          this.spaceRepository.activeSpace?.institution?.settings?.allowAdminsAsObserversInSpace ??
          false,
      ),
    ),
  ]).pipe(
    map(
      ([isObserverModeFlagEnabled, allowAdminsAsObserversInSpace]) =>
        isObserverModeFlagEnabled && allowAdminsAsObserversInSpace,
    ),
  );
  readonly TEST_SPEAKER = 'TEST_SPEAKER';

  deviceErrorType = DeviceErrorType;
  intercomArticles = intercomArticles;
  readonly ColorScheme = ColorScheme;
  readonly isTouchDevice: boolean;

  micActiveBars$ = this.micLevel$.pipe(
    map((micLevel) => this.getMicLevelActiveBars(micLevel)),
    distinctUntilChanged(),
  );

  readonly micLevelBars = Array.from({ length: 24 }, (_, i) => i);

  private getMicLevelActiveBars(level: number | null | undefined): number {
    const safe = Math.max(0, Math.min(100, level ?? 0));
    return Math.min(this.micLevelBars.length, Math.ceil((safe / 100) * this.micLevelBars.length));
  }

  user?: User;

  inCallUsers: User[] = [];

  showMicNoInputPopUp = false;

  deviceType = DevicesType;

  prompts = PROMPTS;

  guides = GUIDES;

  screenType = ViewType;
  musicModeFeatureFlag = false;

  readonly isChildModeActive$ = this.childModeService.isChildModeActive$;

  speakerSelectOpen = false;
  micSelectOpen = false;
  cameraSelectOpen = false;

  @ViewChild('deviceSelectBottomSheetTpl') deviceSelectBottomSheetTpl?: TemplateRef<unknown>;

  deviceKind: DeviceKind | null = null;
  deviceSheetTitle = '';

  private deviceBottomSheetRef?: MatBottomSheetRef<unknown>;

  cameraErrorMessage$ = combineLatest([
    this.localTrackManagerService.camState$,
    this.localTrackManagerService.micState$,
  ]).pipe(
    map(([camState, micState]) => {
      if (!camState.hasError) {
        return undefined;
      }

      if (deviceHasPermissionError(camState) && deviceHasPermissionError(micState)) {
        return this.translateService.instant('No cam and mic access');
      } else if (camState.errorType === DeviceErrorType.NO_INPUT_DETECTED) {
        return this.translateService.instant('No input from camera');
      } else if (camState.errorType === DeviceErrorType.NOT_FOUND) {
        return this.translateService.instant('No camera detected');
      } else if (deviceHasPermissionError(camState)) {
        return this.translateService.instant('No camera access');
      } else if (camState.errorType === DeviceErrorType.GET_USER_MEDIA_TIMEOUT) {
        return this.translateService.instant("We're having trouble starting your camera");
      } else {
        return this.translateService.instant('Camera is not set up correctly');
      }
    }),
  );

  troubleshootErrorMessage$ = combineLatest([
    this.localTrackManagerService.camState$,
    this.localTrackManagerService.micState$,
  ]).pipe(
    map(([camState, micState]) => {
      if (deviceHasPermissionError(camState) && deviceHasPermissionError(micState)) {
        return this.translateService.instant(
          'Allow access so that others can see and hear you on this call',
        );
      } else if (micState.hasError) {
        return this.getTroubleshootMicErrorMessage(micState);
      } else if (camState.hasError) {
        return this.getTroubleshootCamErrorMessage(camState);
      }
    }),
  );

  micErrorMessage$ = combineLatest([this.localTrackManagerService.micState$, this.devices$]).pipe(
    map(([micState, devices]) => this.getDeviceErrorMessage(micState, devices.mics.length)),
    distinctUntilChanged(),
  );
  micShowLabel$ = this.localTrackManagerService.micState$.pipe(
    map((micState) => this.shouldShowDeviceLabel(micState)),
    distinctUntilChanged(),
  );
  micLabel$ = combineLatest([this.selectedMicMatSelect$, this.devices$]).pipe(
    map(([deviceId, devices]) => this.getDeviceLabelFrom(deviceId, DeviceKind.Mic, devices)),
    distinctUntilChanged(),
  );

  speakerShowError$ = combineLatest([this.localTrackManagerService.micState$, this.devices$]).pipe(
    map(
      ([micState, devices]) =>
        micState.hasError &&
        (this.hasPermissionError(micState) ||
          this.hasNotFoundErrorWithDevices(micState, devices.mics.length)),
    ),
    distinctUntilChanged(),
  );
  speakerLabel$ = combineLatest([this.selectedSpeakerMatSelect$, this.devices$]).pipe(
    map(([deviceId, devices]) => this.getDeviceLabelFrom(deviceId, DeviceKind.Speaker, devices)),
    distinctUntilChanged(),
  );

  camErrorMessage$ = combineLatest([this.localTrackManagerService.camState$, this.devices$]).pipe(
    map(([camState, devices]) => this.getDeviceErrorMessage(camState, devices.cameras.length)),
    distinctUntilChanged(),
  );
  camShowLabel$ = this.localTrackManagerService.camState$.pipe(
    map((camState) => this.shouldShowDeviceLabel(camState)),
    distinctUntilChanged(),
  );
  camLabel$ = combineLatest([this.selectedCamMatSelect$, this.devices$]).pipe(
    map(([deviceId, devices]) => this.getDeviceLabelFrom(deviceId, DeviceKind.Camera, devices)),
    distinctUntilChanged(),
  );

  // ========== Select trigger view model streams ==========

  speakerTrigger$ = combineLatest([
    this.localTrackManagerService.micState$,
    this.devices$,
    this.speakerLabel$,
    this.speakerShowError$,
  ]).pipe(
    map(([micState, , label, showError]) => {
      const isDisabled = this.isLoadingDevices || micState.isGettingStream || showError;
      const troubleshootAction = isDisabled && showError ? () => void this.troubleshootMic() : null;
      return {
        icon: isDisabled ? 'speaker_audio-disabled' : 'speaker_audio',
        label: showError ? this.translateService.instant('Permission Required') : label,
        hasError: showError,
        disabled: isDisabled,
        troubleshootAction,
        clickAction: () => {
          if (troubleshootAction) {
            troubleshootAction();
            return;
          }
          if (isDisabled) {
            return;
          }
          this.openDeviceBottomSheet(DeviceKind.Speaker);
        },
      };
    }),
    distinctUntilChanged(),
  );

  micTrigger$ = combineLatest([
    this.localTrackManagerService.micState$,
    this.devices$,
    this.micErrorMessage$,
    this.micShowLabel$,
    this.micLabel$,
  ]).pipe(
    map(([micState, devices, errorMessage, showLabel, label]) => {
      const isDisabled =
        this.isLoadingDevices ||
        micState.isGettingStream ||
        (micState.hasError &&
          (micState.errorType === DeviceErrorType.PERMISSION_DENIED ||
            micState.errorType === DeviceErrorType.PERMISSION_DENIED_BY_SYSTEM ||
            micState.errorType === DeviceErrorType.NOT_FOUND ||
            (micState.errorType === DeviceErrorType.NO_INPUT_DETECTED && devices.mics.length < 2)));
      const troubleshootAction =
        isDisabled && micState.hasError ? () => void this.troubleshootMic() : null;
      return {
        icon: isDisabled ? 'mic-disabled' : 'mic',
        label: errorMessage || (showLabel ? label : ''),
        hasError: micState.hasError,
        disabled: isDisabled,
        troubleshootAction,
        clickAction: () => {
          if (troubleshootAction) {
            troubleshootAction();
            return;
          }
          if (isDisabled) {
            return;
          }
          this.openDeviceBottomSheet(DeviceKind.Mic);
        },
      };
    }),
    distinctUntilChanged(),
  );

  camTrigger$ = combineLatest([
    this.localTrackManagerService.camState$,
    this.devices$,
    this.camErrorMessage$,
    this.camShowLabel$,
    this.camLabel$,
  ]).pipe(
    map(([camState, devices, errorMessage, showLabel, label]) => {
      const isDisabled =
        this.isLoadingDevices ||
        camState.isGettingStream ||
        (camState.hasError &&
          (camState.errorType === DeviceErrorType.PERMISSION_DENIED ||
            camState.errorType === DeviceErrorType.PERMISSION_DENIED_BY_SYSTEM ||
            camState.errorType === DeviceErrorType.NOT_FOUND ||
            (camState.errorType === DeviceErrorType.NO_INPUT_DETECTED &&
              devices.cameras.length < 2)));
      const troubleshootAction =
        isDisabled && camState.hasError ? () => void this.troubleshootCam() : null;
      return {
        icon: isDisabled ? 'videocamera-disabled' : 'videocamera',
        label: errorMessage || (showLabel ? label : ''),
        hasError: camState.hasError,
        disabled: isDisabled,
        troubleshootAction,
        clickAction: () => {
          if (troubleshootAction) {
            troubleshootAction();
            return;
          }
          if (isDisabled) {
            return;
          }
          this.openDeviceBottomSheet(DeviceKind.Camera);
        },
      };
    }),
    distinctUntilChanged(),
  );

  getTroubleshootMicErrorMessage(micState: DeviceState) {
    if (deviceHasPermissionError(micState)) {
      return this.translateService.instant(
        'Allow mic access so that others can hear you on this call',
      );
    } else if (micState.errorType === DeviceErrorType.NO_INPUT_DETECTED) {
      return this.translateService.instant('No input from mic. Troubleshoot below.');
    } else if (micState.errorType === DeviceErrorType.NOT_FOUND) {
      return this.translateService.instant('No mic found. Troubleshoot below');
    } else if (micState.errorType === DeviceErrorType.GET_USER_MEDIA_TIMEOUT) {
      return this.translateService.instant('Could not start mic. Troubleshoot below');
    } else {
      return this.translateService.instant('Mic failed to start. Troubleshoot below');
    }
  }

  getTroubleshootCamErrorMessage(camState: DeviceState) {
    if (deviceHasPermissionError(camState)) {
      return this.translateService.instant(
        'Allow camera access so that others can see you on this call',
      );
    } else if (camState.errorType === DeviceErrorType.NO_INPUT_DETECTED) {
      return this.translateService.instant('No input from camera. Troubleshoot below.');
    } else if (camState.errorType === DeviceErrorType.NOT_FOUND) {
      return this.translateService.instant('No camera found. Troubleshoot below');
    } else if (camState.errorType === DeviceErrorType.GET_USER_MEDIA_TIMEOUT) {
      return this.translateService.instant('Could not start camera. Troubleshoot below');
    } else {
      return this.translateService.instant('Camera failed to start. Troubleshoot below');
    }
  }

  applyTroubleshootPermissionStyle$ = combineLatest([
    this.localTrackManagerService.camState$,
    this.localTrackManagerService.micState$,
  ]).pipe(
    map(([camState, micState]) => {
      if (
        deviceHasPermissionError(micState) ||
        (!micState.hasError && deviceHasPermissionError(camState))
      ) {
        return true;
      }
      return false;
    }),
  );

  constructor(
    private dialogRef: MatDialogRef<JoinCallPanelComponent>,
    private readonly matBottomSheet: MatBottomSheet,
    ngZone: NgZone,
    devicesManagerService: DevicesManagerService,
    uiService: UiService,
    protected userService: UserService,
    rtcServiceController: RtcServiceController,
    private presenceProvider: PresenceProvider,
    deviceDetectorService: DeviceDetectorService,
    protected flagsService: FlagsService,
    wbDialogService: WbDialogService,
    domListenerFactoryService: DomListenerFactoryService,
    private readonly micButtonStateService: MicButtonStateService,
    private readonly camButtonStateService: CamButtonStateService,
    deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    audioService: AudioService,
    protected translateService: TranslateService,
    virtualBackgroundInsertableStreamService: VirtualBackgroundInsertableStreamService,
    sessionSharedDataService: SessionSharedDataService,
    localTrackManagerService: LocalTracksManagerService,
    telemetryService: TelemetryService,
    private noiseCancellationConfigService: NoiseCancellationConfigurationService,
    customerSupportService: CustomerSupportService,
    private childModeService: ChildModeService,
    private musicModeConfigService: MusicModeConfigurationService,
    private spaceRepository: SpaceRepository,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      isStartCall: boolean;
      usersLookUp: { [key: string]: User };
      sessionId: string;
      breakoutRoomId?: string;
      whoStartedCall?: string;
    },
  ) {
    super(
      domListenerFactoryService,
      deviceDetectorService,
      uiService,
      rtcServiceController,
      devicesManagerService,
      ngZone,
      deviceAndBrowserDetectorService,
      audioService,
      PROMPTS.JOIN_CALL,
      translateService,
      virtualBackgroundInsertableStreamService,
      sessionSharedDataService,
      localTrackManagerService,
      telemetryService,
      true,
      flagsService,
      userService,
      customerSupportService,
    );
    this.isTouchDevice = deviceAndBrowserDetectorService.isTouchDevice();
    wbDialogService.pushNewDialogRef<JoinCallPanelComponent>(dialogRef);

    this.userService.user.pipe(untilDestroyed(this)).subscribe((res: any) => {
      this.user = res && res.user;
    });

    if (!data.isStartCall) {
      this.subscribeToCallActivity();
    }
  }

  async ngOnInit() {
    this.musicModeFeatureFlag = this.flagsService.isFlagEnabled(FLAGS.ENABLE_AUDIO_MUSIC_MODE);
    super.ngOnInit();

    this.micButtonStateService.joinPreviewMicUnmuteFailed$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.troubleshootMic());

    this.camButtonStateService.joinPreviewCamUnmuteFailed$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.troubleshootCam());

    this.uiService.isMobile.pipe(untilDestroyed(this)).subscribe((isMobile) => {
      if (!isMobile) {
        this.dismissDeviceBottomSheet();
      }
    });
  }

  ngOnDestroy(): void {
    this.dismissDeviceBottomSheet();
    super.ngOnDestroy();
  }

  dismissDeviceBottomSheet(): void {
    this.deviceBottomSheetRef?.dismiss();
    this.deviceBottomSheetRef = undefined;
  }

  openDeviceBottomSheet(kind: DeviceKind): void {
    if (!this.deviceSelectBottomSheetTpl) {
      return;
    }
    this.deviceKind = kind;
    this.deviceSheetTitle =
      kind === DeviceKind.Speaker
        ? this.translateService.instant('Speakers')
        : kind === DeviceKind.Mic
        ? this.translateService.instant('Microphone')
        : this.translateService.instant('Camera');
    this.deviceBottomSheetRef = this.matBottomSheet.open(this.deviceSelectBottomSheetTpl, {
      panelClass: ['pencil-bottom-sheet'],
      closeOnNavigation: true,
    });
    this.deviceBottomSheetRef
      .afterDismissed()
      .pipe(take(1), untilDestroyed(this))
      .subscribe(() => {
        this.deviceKind = null;
        this.deviceSheetTitle = '';
      });
  }

  async selectDeviceFromBottomSheet(kind: DeviceKind, deviceId: string): Promise<void> {
    switch (kind) {
      case DeviceKind.Speaker:
        await this.selectSpeakerFromBottomSheet(deviceId);
        break;
      case DeviceKind.Mic:
        await this.selectMicFromBottomSheet(deviceId);
        break;
      case DeviceKind.Camera:
        await this.selectCamFromBottomSheet(deviceId);
        break;
    }
  }

  async selectSpeakerFromBottomSheet(value: string): Promise<void> {
    this.selectedSpeakerMatSelect = value;
    await this.setSpeakerPreview(value);
    this.syncSpeakerSelection(value);
    this.dismissDeviceBottomSheet();
  }

  async selectMicFromBottomSheet(value: string): Promise<void> {
    this.selectedMicMatSelect = value;
    await this.setMicPreview(value);
    this.syncMicSelection(value);
    this.dismissDeviceBottomSheet();
  }

  async selectCamFromBottomSheet(value: string): Promise<void> {
    this.selectedCamMatSelect = value;
    await this.setCameraPreview(value);
    this.syncCamSelection(value);
    this.dismissDeviceBottomSheet();
  }

  onTestSpeakerFromBottomSheet(): void {
    const previousValue = this.devices.activeSpeakerId || 'default';
    this.selectedSpeakerMatSelect = previousValue;
    void this.testSpeaker();
  }

  // ovverride
  closeDialog() {
    if (this.childModeService.isChildModeActive) {
      return;
    }
    this.finish(CallRelatedModalAction.NOT_PROCEEDING_WITH_THE_CALL);
  }

  // override
  getComponentPromptLayout(): PROMPTS {
    return this.prompts.JOIN_CALL;
  }

  // override
  showVirtualBackgroundsModal(): void {
    this.finish(CallRelatedModalAction.OPEN_VIRTUAL_BACKGROUND_MODAL);
  }

  handleTestSpeakerClick(event: MouseEvent) {
    event.stopPropagation();
    event.stopImmediatePropagation();
    event.preventDefault();

    const previousValue = this.devices.activeSpeakerId || 'default';
    this.selectedSpeakerMatSelect = previousValue;

    this.testSpeaker();
  }

  // override
  async setSpeakerPreview(id: string | null) {
    if (id === this.TEST_SPEAKER) {
      const previousValue = this.devices.activeSpeakerId || 'default';
      this.selectedSpeakerMatSelect = previousValue;
      return;
    }
    await super.setSpeakerPreview(id);
  }

  private subscribeToCallActivity() {
    this.presenceProvider
      .getCallPresenceActivity(this.data.sessionId, this.data.breakoutRoomId)
      .pipe(untilDestroyed(this))
      .subscribe((inCallUsers) => {
        inCallUsers = inCallUsers ?? new Set<string>();
        if (inCallUsers.size === 0) {
          this.finish(CallRelatedModalAction.NOT_PROCEEDING_WITH_THE_CALL);
        } else {
          const tmpUsers: User[] = [];
          inCallUsers.forEach((userId) => {
            if (this.data.usersLookUp[userId]) {
              tmpUsers.push(this.data.usersLookUp[userId]);
            }
          });
          this.inCallUsers = tmpUsers;
        }
      });
  }

  async finish(action: CallRelatedModalAction) {
    const joinCallDevicesState: JoinCallDevicesState = {
      unmuteCam: false,
      unmuteMic: false,
    };
    // Unmute cam/mic if: camera/mic is on normally, OR camera/mic is loading (isGettingStream)
    // and the user is proceeding with the call so the cam/mic acquisition continues in the background.
    if (
      !this.camState.hasError &&
      ((!this.camState.isGettingStream && this.camState.state) ||
        (this.camState.isGettingStream &&
          action !== CallRelatedModalAction.NOT_PROCEEDING_WITH_THE_CALL))
    ) {
      joinCallDevicesState.unmuteCam = true;
    }

    if (
      !this.micState.hasError &&
      ((!this.micState.isGettingStream && this.micState.state) ||
        (this.micState.isGettingStream &&
          action !== CallRelatedModalAction.NOT_PROCEEDING_WITH_THE_CALL))
    ) {
      joinCallDevicesState.unmuteMic = true;
    }

    this.rtcServiceController.service.isMusicModeEnabled = this.enableMusicMode;
    this.musicModeConfigService.saveMusicModeState(this.enableMusicMode);
    this.rtcServiceController.service.isNoiseCancellationEnabled = this.enableNoiseCancellation;
    this.noiseCancellationConfigService.saveNoiseCancellationState(this.enableNoiseCancellation);
    this.dialogRef.close({ action: action, joinCallDevicesState: joinCallDevicesState });
  }

  openDeviceModal() {
    this.finish(CallRelatedModalAction.OPEN_SPACE_DEVICE_MODAL);
  }

  // Helper methods for error state checks
  hasPermissionError(state: DeviceState): boolean {
    return (
      state.hasError &&
      (state.errorType === DeviceErrorType.PERMISSION_DENIED ||
        state.errorType === DeviceErrorType.PERMISSION_DENIED_BY_SYSTEM)
    );
  }

  hasNotFoundErrorWithDevices(state: DeviceState, deviceCount: number): boolean {
    return state.hasError && state.errorType === DeviceErrorType.NOT_FOUND && deviceCount > 0;
  }

  hasNotFoundErrorWithoutDevices(state: DeviceState, deviceCount: number): boolean {
    return state.hasError && state.errorType === DeviceErrorType.NOT_FOUND && deviceCount === 0;
  }

  hasNoInputOrTimeoutError(state: DeviceState): boolean {
    return (
      state.hasError &&
      (state.errorType === DeviceErrorType.NO_INPUT_DETECTED ||
        state.errorType === DeviceErrorType.GET_USER_MEDIA_TIMEOUT)
    );
  }

  shouldShowDeviceLabel(state: DeviceState): boolean {
    return !state.hasError || state.errorType === DeviceErrorType.DEFAULT_ERROR;
  }

  syncMicSelection(value: string): void {
    this.selectedMicMatSelect = value;
    this.emitSelectedMatSelectValues();
  }

  syncSpeakerSelection(value: string): void {
    this.selectedSpeakerMatSelect = value;
    this.emitSelectedMatSelectValues();
  }

  syncCamSelection(value: string): void {
    this.selectedCamMatSelect = value;
    this.emitSelectedMatSelectValues();
  }

  getDeviceErrorMessage(state: DeviceState, deviceCount: number): string | null {
    if (!state.hasError) {
      return null;
    }

    if (this.hasPermissionError(state) || this.hasNotFoundErrorWithDevices(state, deviceCount)) {
      return this.translateService.instant('Permission Required');
    }

    if (
      this.hasNoInputOrTimeoutError(state) ||
      this.hasNotFoundErrorWithoutDevices(state, deviceCount)
    ) {
      return this.translateService.instant('No input detected');
    }

    return null;
  }

  private getDeviceLabelFrom(
    deviceId: string | null | undefined,
    deviceKind: DeviceKind,
    devices: RTCDevices,
  ): string {
    if (!deviceId || deviceId === this.LOADING) {
      return this.translateService.instant('Loading...');
    }

    if (deviceKind === DeviceKind.Speaker && deviceId === 'default') {
      return this.translateService.instant('System Default');
    }

    const deviceList =
      deviceKind === DeviceKind.Speaker
        ? devices.speakers
        : deviceKind === DeviceKind.Mic
        ? devices.mics
        : devices.cameras;

    const device = deviceList?.find((d) => d.deviceId === deviceId);
    return device?.label || deviceId;
  }

  openObserverSupportLink() {
    this.customerSupportService.openLink(this.observerModeLearnMoreUrl);
  }
}
